import { HttpClient, HttpHeaders, HttpParams, HttpRequest, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CORE_CONFIG_TOKEN, ICoreConfig } from '@app/core/configs/core.config';
import { IErrorResponseBody, IRequestOptions, IResponseBody, RequestMethods } from '@app/core/models/http';
import { isObjectLike, isString } from 'lodash';
import { throwError } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { catchError, filter, map, tap } from 'rxjs/operators';

const DEFAULT_REQUEST_OPTIONS: IRequestOptions = {
  avoidBlockUI: false,
  showLoading: true,
  headers: {
    'Content-Type': 'application/json;charset=utf-8',
  },
  responseType: 'json',
  timeout: false,
};

@Injectable()
export class HttpService {
  constructor(private http: HttpClient, @Inject(CORE_CONFIG_TOKEN) private coreConfig: ICoreConfig) {}

  request<T, P>(
    method: RequestMethods,
    url: string,
    data: P | null,
    params: P | null,
    options: IRequestOptions,
  ): Observable<T> {
    const fullUrl = `${this.coreConfig.API_BASE}${url}`;
    const mergedOptions: IRequestOptions = {
      ...DEFAULT_REQUEST_OPTIONS,
      ...options,
    };

    const httpOptions: any = {
      headers: new HttpHeaders(mergedOptions.headers),
      responseType: mergedOptions.responseType,
    };

    if (params) {
      if (isString(params)) {
        httpOptions.params = new HttpParams({ fromString: params });
      } else if (isObjectLike(params)) {
        httpOptions.params = new HttpParams({ fromObject: params as any });
      }
    }

    const req = new HttpRequest<P>(method.toString(), fullUrl, data, httpOptions);

    return this.http.request<IResponseBody<T>>(req).pipe(
      filter(r => r instanceof HttpResponse),
      tap<HttpResponse<IResponseBody<T>>>(res => {
        if (res.body) {
          const body = res.body as any;
          if (body.errorMsg || (body.resultCode && body.resultCode !== 200)) {
            throw {
              error: body,
              status: 500,
              code: body.resultCode,
              message: body.errorMsg,
              url: res.url,
            };
          }
        }
      }),
      map<HttpResponse<IResponseBody<T>>, T>(res => {
        return (res.body.data || res.body) as T;
      }),
      catchError((error: IErrorResponseBody) => {
        return throwError(error);
      }),
    );
  }

  get<T = any, P = any>(url: string, data?: P, options?: IRequestOptions): Observable<T> {
    return this.request<T, P>(RequestMethods.GET, url, null, data, options);
  }

  post<T = any, P = any>(url: string, data?: P, options?: IRequestOptions): Observable<T> {
    return this.request<T, P>(RequestMethods.POST, url, data, null, options);
  }

  delete<T = any, P = any>(url: string, data?: P, options?: IRequestOptions): Observable<T> {
    return this.request<T, P>(RequestMethods.DELETE, url, null, data, options);
  }

  put<T = any, P = any>(url: string, data?: P, options?: IRequestOptions): Observable<T> {
    return this.request<T, P>(RequestMethods.PUT, url, data, null, options);
  }

  patch<T = any, P = any>(url: string, data?: P, options?: IRequestOptions): Observable<T> {
    return this.request<T, P>(RequestMethods.PATCH, url, data, null, options);
  }
}
